// src/components/Receiver/ReceiverNav.js
import React, { useMemo, useState } from "react";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Typography,
  Box,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useRole } from "../../contexts/RoleContext";
import {
  useTabPermissions,
  PERMISSION_MAP,
} from "../../hooks/useTabPermissions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTruckFast,
  faCalendarDays,
  faChartLine,
  faChartPie,
  faLeaf,
  faHandshake,
  faLock,
  faCircleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { ROLE_COLORS } from "../../constants/roles";

import { useScheduleData } from "../../hooks/useScheduleData";

function ReceiverNav({ collapsed }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { currentRole } = useRole();
  const { hasTabPermission } = useTabPermissions();
  const [toast, setToast] = useState({ open: false, message: "" });

  const currentColor = ROLE_COLORS[currentRole]?.primary || "#2196f3";

  const { rows } = useScheduleData();

  const pendingCount = useMemo(() => {
    return rows.filter((r) => r.status === "pending").length;
  }, [rows]);

  const showScheduleAlert = pendingCount > 0;

  const navItems = [
    {
      label: t("Browse Materials"),
      path: "/receiver/material-management",
      icon: faTruckFast,
    },
    {
      label: t("Schedule"),
      path: "/receiver/schedule",
      icon: faCalendarDays,
    },
    {
      label: t("Partnerships"),
      path: "/partnerships",
      icon: faHandshake,
    },
    {
      label: t("Inventory"),
      path: "/receiver/inventory",
      icon: faChartLine,
    },
    {
      label: t("Flow Analysis"),
      path: "/receiver/flow-analysis",
      icon: faChartPie,
    },
    {
      label: t("ESG & Carbon"),
      path: "/receiver/ghg",
      icon: faLeaf,
    },
  ];

  if (navItems.length === 0) return null;

  const handleNavigation = (item, hasPermission) => {
    if (hasPermission) navigate(item.path);
    else {
      setToast({
        open: true,
        message: t("You don't have access to this feature"),
      });
    }
  };

  const handleCloseToast = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  // Small visual "!" badge (for collapsed mode)
  const ExclaimBadge = ({ tooltip }) => (
    <Tooltip title={tooltip} placement="right" arrow>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 16,
          height: 16,
          borderRadius: "999px",
          backgroundColor: "rgba(244, 67, 54, 0.95)",
          color: "#fff",
          fontSize: "11px",
          fontWeight: 900,
          lineHeight: 1,
          boxShadow: "0 6px 14px rgba(244, 67, 54, 0.25)",
        }}
      >
        !
      </Box>
    </Tooltip>
  );

  return (
    <>
      {!collapsed && (
        <Box className="nav-section-header">
          <Typography
            className="nav-section-title"
            sx={{ color: `${currentColor} !important` }}
          >
            {t("Features")}
          </Typography>
        </Box>
      )}

      <List sx={{ pt: 0, pb: 0 }}>
        {navItems.map((item, index) => {
          const permissionInfo = PERMISSION_MAP[item.path];
          const hasPermission = permissionInfo
            ? hasTabPermission(permissionInfo.section, permissionInfo.permission)
            : true;

          const isActive = location.pathname === item.path;
          const isDisabled = !hasPermission;

          const isScheduleItem = item.path === "/receiver/schedule";
          const shouldShowScheduleAlert =
            isScheduleItem && showScheduleAlert && !isDisabled;

          return (
            <ListItem
              key={index}
              disablePadding
              sx={{
                margin: "2px 8px",
                borderRadius: "8px",
                width: "auto",
              }}
            >
              <Tooltip
                title={
                  isDisabled
                    ? t("You don't have access to this feature")
                    : collapsed
                      ? item.label
                      : ""
                }
                placement="right"
                arrow
              >
                <span style={{ width: "100%" }}>
                  <ListItemButton
                    onClick={() => handleNavigation(item, hasPermission)}
                    sx={{
                      justifyContent: collapsed ? "center" : "flex-start",
                      px: collapsed ? 0 : 2,
                      py: 1.25,
                      borderRadius: "8px",
                      minHeight: "44px",
                      opacity: isDisabled ? 0.5 : 1,
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      backgroundColor:
                        isActive && !isDisabled
                          ? "rgba(33, 150, 243, 0.18)"
                          : "transparent",
                      "&:hover": {
                        backgroundColor:
                          isActive && !isDisabled
                            ? "rgba(33, 150, 243, 0.22)"
                            : isDisabled
                              ? "rgba(255, 255, 255, 0.03)"
                              : "rgba(255, 255, 255, 0.06)",
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: collapsed ? "auto" : 40,
                        justifyContent: "center",
                        color: isDisabled
                          ? "#666"
                          : isActive
                            ? currentColor
                            : "#b0b0b0",
                        fontSize: "18px",
                      }}
                    >
                      <Box sx={{ position: "relative", display: "inline-flex" }}>
                        <FontAwesomeIcon icon={item.icon} />
                        {collapsed && shouldShowScheduleAlert && (
                          <Box sx={{ position: "absolute", top: -6, right: -10 }}>
                            <ExclaimBadge
                              tooltip={`${pendingCount} ${t(
                                "pending request(s)",
                                "pending request(s)"
                              )}`}
                            />
                          </Box>
                        )}
                      </Box>
                    </ListItemIcon>

                    {!collapsed && (
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              width: "100%",
                            }}
                          >
                            <span>{item.label}</span>

                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              {shouldShowScheduleAlert && (
                                <Tooltip
                                  title={`${pendingCount} ${t(
                                    "pending request(s)",
                                    "pending request(s)"
                                  )}`}
                                  placement="right"
                                  arrow
                                >
                                  <Box
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      color: "rgba(244, 67, 54, 0.95)",
                                    }}
                                  >
                                    <FontAwesomeIcon
                                      icon={faCircleExclamation}
                                      style={{ fontSize: "14px" }}
                                    />
                                  </Box>
                                </Tooltip>
                              )}

                              {isDisabled && (
                                <FontAwesomeIcon
                                  icon={faLock}
                                  style={{
                                    fontSize: "12px",
                                    marginLeft: "4px",
                                    opacity: 0.7,
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        }
                        sx={{
                          "& .MuiTypography-root": {
                            fontFamily: "Poppins, sans-serif",
                            fontSize: "0.9375rem",
                            color: isDisabled
                              ? "#666"
                              : isActive
                                ? currentColor
                                : "#e0e0e0",
                            fontWeight: isActive && !isDisabled ? 600 : 400,
                          },
                        }}
                      />
                    )}
                  </ListItemButton>
                </span>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseToast}
          severity="warning"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default ReceiverNav;
